import { IsString } from "class-validator";
import { IsBaseUrl } from "@utils/validators/is-base-url.decorator";

import { AutoValidated, throwIfFalsy } from "@/autovalidate";

import { IsStoryblokEnvironment, type StoryblokEnvironment } from "./types";

// If you've updated this, remember to also update the build inputs in project.json
/** Env config that can be exposed to the client side.
 * Anything in here is also accessible serverside.
 * Note that only env variables starting with NEXT_PUBLIC can be
 * read client-side */
class PublicRoutesConfiguration extends AutoValidated {

  @IsString()
  public NEXT_PUBLIC_WEBSITE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_WEBSITE_URL,
    "NEXT_PUBLIC_WEBSITE_URL",
  );

  @IsString()
  public NEXT_PUBLIC_FORM_ACTIONS_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_FORM_ACTIONS_URL,
    "NEXT_PUBLIC_FORM_ACTIONS_URL",
  );

  @IsString()
  public NEXT_PUBLIC_FORMS_STORAGE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_FORMS_STORAGE_URL,
    "NEXT_PUBLIC_FORMS_STORAGE_URL",
  );

  @IsString()
  public NEXT_PUBLIC_FORMS_STRUCTURE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_FORMS_STRUCTURE_URL,
    "NEXT_PUBLIC_FORMS_STRUCTURE_URL",
  );

  @IsBaseUrl()
  public NEXT_AUTH_API_BASE_URL: string = throwIfFalsy(
    process.env["NEXT_PUBLIC_AUTH_API_BASE_URL"],
    "NEXT_PUBLIC_AUTH_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_BLOG_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_BLOG_API_BASE_URL,
    "NEXT_PUBLIC_BLOG_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_OLD_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_OLD_API_BASE_URL,
    "NEXT_PUBLIC_OLD_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CAPI_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CAPI_BASE_URL,
    "NEXT_PUBLIC_CAPI_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_ORDERS_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_ORDERS_BASE_URL,
    "NEXT_PUBLIC_ORDERS_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_BREEDERS_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_BREEDERS_API_BASE_URL,
    "NEXT_PUBLIC_BREEDERS_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_STATISTICS_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_STATISTICS_URL,
    "NEXT_PUBLIC_STATISTICS_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CHECKOUT_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CHECKOUT_URL,
    "NEXT_PUBLIC_CHECKOUT_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_FUNNEL_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_FUNNEL_URL,
    "NEXT_PUBLIC_FUNNEL_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_DISCOUNT_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_DISCOUNT_API_BASE_URL,
    "NEXT_PUBLIC_DISCOUNT_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_SURVEY_ANSWERS_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_SURVEY_ANSWERS_BASE_URL,
    "NEXT_PUBLIC_SURVEY_ANSWERS_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_SUF_QUIZ_ANSWERS_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_SUF_QUIZ_ANSWERS_BASE_URL,
    "NEXT_PUBLIC_SUF_QUIZ_ANSWERS_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_ADDRESSES_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_ADDRESSES_BASE_URL,
    "NEXT_PUBLIC_ADDRESSES_BASE_URL",
  );

  @IsString()
  public SECURITY_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_SECURITY_API_BASE_URL,
    "NEXT_PUBLIC_SECURITY_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_PAYMENT_METHOD_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_PAYMENT_METHOD_API_BASE_URL ?? "http://localhost:9010",
    "NEXT_PUBLIC_PAYMENT_METHOD_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_DELIVERY_DATES_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_DELIVERY_DATES_API_BASE_URL,
    "NEXT_PUBLIC_DELIVERY_DATES_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL,
    "NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CATS_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CATS_BASE_URL,
    "NEXT_PUBLIC_CATS_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CUSTOMER_MANAGEMENT_API_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CUSTOMER_MANAGEMENT_API_BASE_URL ?? "http://customer-management.customer-management",
    "NEXT_PUBLIC_CUSTOMER_MANAGEMENT_API_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CHARGES_BASE_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CHARGES_BASE_URL,
    "NEXT_PUBLIC_CHARGES_BASE_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_CUSTOMER_PORTAL_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_CUSTOMER_PORTAL_URL,
    "NEXT_PUBLIC_CUSTOMER_PORTAL_URL",
  );

  @IsBaseUrl()
  public NEXT_PUBLIC_ADMIN_PORTAL_URL: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_ADMIN_PORTAL_URL,
    "NEXT_PUBLIC_ADMIN_PORTAL_URL",
  );

  @IsString()
  public NEXT_PUBLIC_COOKIE_DOMAIN: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_ADMIN_COOKIE_DOMAIN,
    "NEXT_PUBLIC_ADMIN_COOKIE_DOMAIN",
  );

  @IsStoryblokEnvironment()
  public readonly NEXT_PUBLIC_STORYBLOK_ENV: StoryblokEnvironment = throwIfFalsy(
    process.env.NEXT_PUBLIC_STORYBLOK_ENV,
    "NEXT_PUBLIC_STORYBLOK_ENV",
  ) as StoryblokEnvironment;

  @IsString()
  public readonly NEXT_PUBLIC_STORYBLOK_API_TOKEN: string = throwIfFalsy(
    process.env.NEXT_PUBLIC_STORYBLOK_API_TOKEN,
    "NEXT_PUBLIC_STORYBLOK_API_TOKEN",
  );

}

const BasePublicRoutesConfig = PublicRoutesConfiguration;
const PublicRoutesConfig = PublicRoutesConfiguration.create();

export {
  PublicRoutesConfig,
  BasePublicRoutesConfig,
};
